/**
 * Purchase order API and form types.
 * Contract: see repo root `purchase-orders-api.md`.
 */

/** PO lifecycle status returned by the API. */
export type PurchaseOrderStatus =
  | "draft"
  | "approved"
  | "sent"
  | "partially_received"
  | "received"
  | "closed"
  | "cancelled";

/** Dropdown `{ value, label }` used on read/write across PO endpoints. */
export interface DropdownOption {
  value: number | string;
  label?: string;
}

export interface PurchaseOrderSupplierRef {
  id: number;
  name?: string;
}

export interface PurchaseOrderUserRef {
  id: number;
  name?: string;
}

/** Saved line item nested under `purchase_order.items` on detail load. */
export interface PurchaseOrderSavedLineItem {
  id?: number;
  purchase_order_id?: number;
  assembly_item_id: number;
  supplier_plu?: string | null;
  description?: string | null;
  ordered_amount?: string | number;
  unit_of_measure?: DropdownOption | null;
  unit_cost?: string | number | null;
  lot_number?: string | null;
  notes?: string | null;
  assembly_item?: {
    id: number;
    product_name?: string;
  };
}

/** Top-level `items` on detail load — receiving summary per line. */
export interface PurchaseOrderReceivingSummaryLine {
  purchase_order_item_id?: number;
  id?: number;
  assembly_item_id?: number;
  product_name?: string;
  supplier_plu?: string;
  ordered_amount?: number | string;
  received_amount?: number;
  remaining_amount?: number;
  over_received_amount?: number;
  status?: PurchaseOrderStatus | string;
  unit_of_measure?: DropdownOption | null;
  unit_cost?: string | number | null;
  lot_number?: string | null;
  description?: string | null;
  notes?: string | null;
}

export interface PurchaseOrderReceivingInvoice {
  id: number;
  invoice_number?: string;
  date?: string;
  received_amount?: number;
  location?: DropdownOption | null;
}

export interface PurchaseOrderSummary {
  ordered_amount?: number;
  received_amount?: number;
  remaining_amount?: number;
  completion_percent?: number;
  unit_of_measure?: number;
}

export interface PurchaseOrderDetail {
  id: number;
  po_number?: string | null;
  supplier_id?: number;
  created_by_user_id?: number;
  approved_by_user_id?: number | null;
  order_date?: string;
  expected_delivery_date?: string;
  status?: PurchaseOrderStatus;
  notes?: string | null;
  approved_at?: string | null;
  sent_at?: string | null;
  closed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  location?: DropdownOption | null;
  supplier?: PurchaseOrderSupplierRef;
  created_by?: PurchaseOrderUserRef;
  approved_by?: PurchaseOrderUserRef | null;
  items?: PurchaseOrderSavedLineItem[];
}

/** `POST /purchaseOrders/{id}` and status-action success bodies. */
export interface PurchaseOrderDetailResponse {
  purchase_order?: PurchaseOrderDetail;
  items?: PurchaseOrderReceivingSummaryLine[];
  receiving_invoices?: PurchaseOrderReceivingInvoice[];
  summary?: PurchaseOrderSummary;
  message?: string;
}

/** `POST /createPurchaseOrder` success body. */
export interface CreatePurchaseOrderResponse {
  purchase_order?: PurchaseOrderDetail;
  id?: number;
  purchase_order_id?: number;
  message?: string;
}

/** Row shape for `POST /purchaseOrders` list search. */
export interface PurchaseOrderListRow {
  id: number;
  po_number?: string | null;
  supplier_name?: string;
  supplier?: PurchaseOrderSupplierRef;
  expected_delivery_date?: string | null;
  status?: PurchaseOrderStatus;
  ordered_amount?: string | number;
  received_amount?: string | number;
  total_cost?: number | string | null;
  ordered_total?: number | string | null;
}

export interface PurchaseOrderListResult {
  data: PurchaseOrderListRow[];
  total: number;
  current_page?: number;
  last_page?: number;
}

/** Raw assembly item from `POST /allAssemblyItems`. */
export interface AssemblyItemDropdownRaw {
  id: number;
  product_name?: string;
  name?: string;
  manufacturer_plu?: string;
  plu?: string;
  supplier_plu?: string;
  manufacturer_id?: number;
  description?: string;
  unit_of_measure?: number | DropdownOption;
  unit_of_measure_text?: string;
  cost?: string | number;
}

export interface AssemblyItemDropdownOption {
  value: number;
  label: string;
  raw?: AssemblyItemDropdownRaw;
}

/** Frontend header form state (create/edit screen). */
export interface PurchaseOrderFormState {
  supplier_id: number | string | null;
  supplier_label?: string;
  order_date: string;
  expected_delivery_date: string;
  location: DropdownOption | null;
  notes: string;
}

/** Frontend editable line row (distinct from API summary/saved shapes). */
export interface PurchaseOrderFormLineItem {
  id?: number | null;
  temp_id: number | string;
  assembly_item_id: number | null;
  item_name: string;
  supplier_plu: string;
  description: string;
  ordered_quantity: string;
  ordered_cartons: string;
  unit_of_measure: DropdownOption | null;
  unit_cost: string;
  expected_lot_number: string;
  notes: string;
  received_amount?: number;
  line_locked?: boolean;
  lineTotal?: number | null;
}

export interface PurchaseOrderLineSummary {
  lineCount: number;
  totalQty: number;
  totalCartons: number;
  estimatedCost: number | null;
}

export type PurchaseOrderValidationResult =
  | { valid: true }
  | { valid: false; message: string };

/** Loose API response shape for id extraction (create/load/status actions). */
export interface PurchaseOrderIdResponse {
  purchase_order?: { id?: number };
  id?: number;
  purchase_order_id?: number;
}

/** Extract PO id from create/load/status-action response payloads. */
export function purchaseOrderIdFromResponse(
  data: PurchaseOrderIdResponse | null | undefined,
): number | null {
  if (!data) return null;
  const id =
    data.purchase_order?.id ?? data.id ?? data.purchase_order_id ?? null;
  return id != null ? Number(id) : null;
}

/** Normalize paginated list response from `POST /purchaseOrders`. */
export function parsePurchaseOrderListResponse(
  data: unknown,
): PurchaseOrderListResult {
  if (!data || typeof data !== "object") {
    return { data: [], total: 0 };
  }
  const record = data as Record<string, unknown>;
  if (Array.isArray(record.data)) {
    return {
      data: record.data as PurchaseOrderListRow[],
      total: Number(record.total ?? record.data.length),
      current_page:
        record.current_page != null ? Number(record.current_page) : undefined,
      last_page:
        record.last_page != null ? Number(record.last_page) : undefined,
    };
  }
  if (Array.isArray(record.purchase_orders)) {
    return {
      data: record.purchase_orders as PurchaseOrderListRow[],
      total: Number(record.total ?? record.purchase_orders.length),
    };
  }
  return { data: [], total: 0 };
}
